import {
  AnimatedNavigationSwitch,
  FullPageCard,
  LazyPage,
  NavigationPage,
} from '@/app/components'
import { programRequirementsSheetConfig } from '@/app/components/ProgramRequirementsSheet/ProgramRequirementsSheet'
import { useProgramRequirements } from '@/app/hooks/useProgramRequirements'
import {
  LocationTag,
  pageWithErrorBoundary,
} from '@/authenticated/components/ErrorBoundary'
import { LoadingAnimation } from '@/components'
import { useBottomSheet } from '@/context/bottom-sheet-context'
import { programsNewCollection } from '@/reducers/programsNew'
import { searchableUtilityProgramsCollection } from '@/reducers/utilityPrograms'
import { UtilityProgram } from '@/types/utilityProgram'
import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { Route, useLocation, useParams } from 'react-router-dom'
import { useCurrentMultiProgramFlowContext } from '@/app/context/MultiProgram/MultiProgramFlowContexts'
import { useAppDispatch } from '@/hooks'

const UtilityProgramDeviceSelection = React.lazy(
  () => import('./device-selection/device-selection'),
)
const DeviceSelectionPage = pageWithErrorBoundary(
  LazyPage(UtilityProgramDeviceSelection),
  {
    location: LocationTag.UtilityProgramDeviceSelection,
    functionalityDescription: 'Utility Program Device Selection Page',
    severity: 'critical',
  },
)
const UtilitySelection = React.lazy(
  () => import('./utility-selection/utility-selection'),
)
const UtilitySelectionPage = pageWithErrorBoundary(LazyPage(UtilitySelection), {
  location: LocationTag.UtilitySelection,
  functionalityDescription: 'Utility Selection Page',
  severity: 'critical',
})
const ReviewEnrollment = React.lazy(
  () =>
    import(
      '@/app/pages/multi-program-enrollment/review-program/review-program'
    ),
)
const ReviewEnrollmentPage = pageWithErrorBoundary(LazyPage(ReviewEnrollment), {
  location: LocationTag.ProgramEnrollment,
  functionalityDescription: 'Program Enrollment Confirmation Page',
  severity: 'critical',
})
const ReviewTOS = React.lazy(() => import('./tos-review/tos-review'))
const ReviewTOSPage = pageWithErrorBoundary(LazyPage(ReviewTOS), {
  location: LocationTag.ReviewTOS,
  functionalityDescription: 'Review Terms of Service Page',
  severity: 'critical',
})

const EnrollmentState = React.lazy(
  () => import('./enrollment-state/enrollment-state'),
)
const EnrollmentStatePage = pageWithErrorBoundary(LazyPage(EnrollmentState), {
  location: LocationTag.EnrollmentState,
  functionalityDescription: 'Enrollment State Page',
  severity: 'critical',
})

const SurveyCompletion = React.lazy(
  () => import('./survey-completion/survey-completion'),
)

const SurveyCompletionRoute = pageWithErrorBoundary(
  LazyPage(SurveyCompletion),
  {
    location: LocationTag.UtilityProgramSurveyCompletion,
    functionalityDescription: 'Survey Completion Routes',
    severity: 'critical',
  },
)

const AccountNumber = React.lazy(() => import('./account-number/AccountNumber'))
const AccountNumberPage = pageWithErrorBoundary(LazyPage(AccountNumber), {
  location: LocationTag.UtilityAccountNumber,
  functionalityDescription: 'Utility Account Number Entry Page',
  severity: 'critical',
})

type UtilityProgramContextType = {
  utilityProgram: UtilityProgram
}

const UtilityProgramContext = createContext<UtilityProgramContextType>({
  utilityProgram: {} as UtilityProgram,
})

export const useMultiProgramRouteContext = () =>
  useContext(UtilityProgramContext)

export default function MultiProgramEnrollmentFlow() {
  const { id: utilityProgramId } = useParams<{ id: string }>()
  const { data: utilityPrograms, isLoadingOrIdle: isLoading } =
    searchableUtilityProgramsCollection.useFetch({
      path: `utility_programs/${utilityProgramId}`,
    })
  const utilityProgram =
    utilityPrograms.length > 0 ? utilityPrograms[0] : undefined

  if (!utilityProgram || isLoading) {
    return (
      <FullPageCard>
        <NavigationPage id="multi-program-page-loading" className="!p-0">
          <div className="flex flex-col gap-4 h-full">
            <section className="h-full">
              <div className={'flex justify-center items-center h-full'}>
                <LoadingAnimation type="falling" />
              </div>
            </section>
          </div>
        </NavigationPage>
      </FullPageCard>
    )
  }

  return (
    <UtilityProgramContext.Provider value={{ utilityProgram }}>
      <MultiProgramEnrollmentRoot />
    </UtilityProgramContext.Provider>
  )
}

function MultiProgramEnrollmentRoot() {
  const dispatch = useAppDispatch()
  const { utilityProgram } = useMultiProgramRouteContext()
  const { programRequirements, saveForLater, routeToPreviousStep } =
    useProgramRequirements(utilityProgram)
  const location = useLocation()
  const { isLoading } = programsNewCollection.useFetch()
  const { flow, flowStepConfig } = useCurrentMultiProgramFlowContext()
  const stepIndex = useMemo(() => {
    if (!programRequirements) {
      return 0
    }
    return (
      programRequirements.steps.findIndex((s) =>
        location.pathname.includes(s.path),
      ) + 1
    )
  }, [programRequirements, location.pathname])
  const { setBottomSheetContent, config: bottomSheetConfig } = useBottomSheet()
  useEffect(() => {
    // load bottom sheet config for the program if it doesn't exist
    if (!bottomSheetConfig && utilityProgram) {
      setBottomSheetContent(
        programRequirementsSheetConfig(
          {
            utilityProgram,
          },
          flow,
        ),
        true,
      )
    }
  }, [])
  const progressBar = useMemo(() => {
    if (
      !programRequirements ||
      location.pathname.includes('/enrollment-state')
    ) {
      return
    }
    return {
      totalSteps: programRequirements.numberOfSteps,
      completedSteps: stepIndex,
    }
  }, [stepIndex, programRequirements, location.pathname])

  useEffect(() => {
    // fetch program requirements one last time before enrolling
    if (location.pathname.includes('/review-enrollment')) {
      dispatch(programsNewCollection.actions.fetch())
    }
  }, [location.pathname])

  return (
    <FullPageCard>
      <NavigationPage
        id="multi-program-enrollment-root"
        navigationOptions={
          !location.pathname.includes('enrollment-state')
            ? ['back', 'close']
            : ['nothing']
        }
        backOverride={routeToPreviousStep}
        onCloseClick={saveForLater}
        backDisabled={flowStepConfig.backDisabled(stepIndex)}
        className="!p-0"
        progressBar={progressBar}
      >
        <div className="flex flex-col gap-4 h-full">
          <section className="h-full">
            {isLoading ? (
              <div className={'flex justify-center items-center h-full'}>
                <LoadingAnimation type="falling" />
              </div>
            ) : (
              <AnimatedNavigationSwitch>
                <Route
                  path={[
                    '/multi-program-enrollment/:id/device-selection',
                    '/onboarding/multi-program-enrollment/:id/device-selection',
                  ]}
                >
                  <DeviceSelectionPage />
                </Route>
                <Route
                  path={[
                    '/multi-program-enrollment/:id/utility-selection',
                    '/onboarding/multi-program-enrollment/:id/utility-selection',
                  ]}
                >
                  <UtilitySelectionPage />
                </Route>
                <Route
                  path={[
                    '/multi-program-enrollment/:id/review-enrollment',
                    '/onboarding/multi-program-enrollment/:id/review-enrollment',
                  ]}
                >
                  <ReviewEnrollmentPage />
                </Route>
                <Route
                  path={[
                    '/multi-program-enrollment/:id/review-tos',
                    '/onboarding/multi-program-enrollment/:id/review-tos',
                  ]}
                >
                  <ReviewTOSPage />
                </Route>
                <Route
                  path={[
                    '/multi-program-enrollment/:id/account-number',
                    '/onboarding/multi-program-enrollment/:id/account-number',
                  ]}
                >
                  <AccountNumberPage />
                </Route>
                <Route
                  path={[
                    '/multi-program-enrollment/:id/enrollment-state/:state',
                    '/onboarding/multi-program-enrollment/:id/enrollment-state/:state',
                  ]}
                >
                  <EnrollmentStatePage />
                </Route>
                <Route
                  path={[
                    '/multi-program-enrollment/:id/survey-completion/*',
                    '/onboarding/multi-program-enrollment/:id/survey-completion/*',
                  ]}
                >
                  <SurveyCompletionRoute />
                </Route>
              </AnimatedNavigationSwitch>
            )}
          </section>
        </div>
      </NavigationPage>
    </FullPageCard>
  )
}
